import { ChangeDetectorRef, Component, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { CommodityService } from '../../../models/commodity-type/service';
import { eCommodityTypeCalculators, ICommodityType } from '../../../models/commodity-type/interfaces';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormValidation } from '../../../classes/formValidation';
import { Coordinates } from '../../../classes/Coordinates';
import { CropTypeService } from '../../../models/crop-type/service';
import { takeUntil } from 'rxjs/operators';
import { ICropTypeListItem, ICropTypeViewModel } from '../../../models/crop-type/interfaces';
import { DateUtility } from '../../../classes/dateUtility';
import { AgmMap } from '@agm/core';
import { MapControlDirective } from '../../../directives/map-control.directive';
import { IAgmMapClick } from '../../shared/interfaces';
import { GeoLocation } from '../../../classes/geolocation.class';
import { navigationViews, UpdateService } from '../../../services/update.service';
import { NotificationService } from '../../../services/notification.service';
import { eIrrigationMethods, eNotificationTypes } from '../../../interfaces/constants';
import { WeatherStationsService } from '../../../models/weather-station-stats/service';
import { IMapIcon, IMapIconSize } from '../../ranch-settings/modals/weather-station/interfaces';
import { IWeatherStationPublicListItem } from '../../weather-stations/interfaces';
import { CropType } from '../../../models/crop-type/cropType';
import { ISelectOptions } from '../../../interfaces/abstract';
import { PlantingAdvancedEdit } from '../../planting-settings/plantingAdvancedEdit';
import { TranslateService } from '../../localization/service';
import { PersistentDatabase } from '../../../services/persistent-database';
import { ETCalculatorResultsInputParams } from '../et-calculator/et-calculator-results.component';
import { EvaporationRecordSimple,
	IrrigationRecommendationSimpleModel,
	SingleCallIrrigationRecommendationModel } from '../../irrigation-event/interfaces';
import { IrrigationService } from '../../irrigation-event/service';
import { HighChartsService } from '../../../classes/highCharts.service';
import { IHighChartCustomSeries } from '../../available-water-chart/interfaces';
import * as Highcharts from 'highcharts';
import { IPlantingSettingsCalculatorSettings } from '../../planting-settings/interfaces';
import { CalculatorSettingsForm } from '../../planting-settings/calculatorSettingsForm';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DripApplicationRateTreeComponent } from '../../planting-settings/sections/drip-application-rate-tree';
import { DripApplicationRateComponent } from '../../planting-settings/sections/drip-application-rate';
import { SprinklerApplicationRateComponent } from '../../planting-settings/sections/sprinkler-calculator/sprinkler-application-rate';
import { MicroSprinklerComponent } from '../../planting-settings/micro-sprinkler/micro-sprinkler';
import { Planting } from '../../planting-settings/planting';

export type ETCalculatorFields = 'AgeOfCrop' | 'CommodityTypeId' | 'CropTypeId' | 'WetDate'
	| 'HarvestDate' | 'EventDate' | 'PreviousDate' | 'DistributionUniformity' |
	'IrrigationMethodId' | 'ApplicationRate' | 'Latitude' | 'Longitude' | 'WeatherStationId' |
	'Company';

@Component({
	selector: 'ir-calculator',
	templateUrl: 'ir-calculator.component.html',
	styleUrls: [ 'ir-calculator.component.scss']
})

export class IrrigationRecommendationCalculatorComponent implements OnInit, OnDestroy {

	@ViewChild(AgmMap, { static: false }) public map: AgmMap;
	@ViewChild(MapControlDirective, { static: false }) private mapControl: MapControlDirective;
	@ViewChild('singleCallEvapChart', { static: false }) private _singleCallEvapChart: ElementRef;

	public commodityTypes: ICommodityType[];
	public cropTypes: ICropTypeListItem[];
	public weatherStations: IWeatherStationPublicListItem[];
	public coordinatesUser: Coordinates; // used to store user coodinates
	public mapZoom: number;
	public ageOfCropOptions: ISelectOptions[];
	public singleCallResult: IrrigationRecommendationSimpleModel;
	/** Daily evaporation rows for the recommendation window (chart uses ETo + rain). */
	public singleCallEvapRecords: EvaporationRecordSimple[] = [];
	public isSaving = false;
	public hours: number;
	public minutes: number;
	public calculatorSettings: IPlantingSettingsCalculatorSettings;
	public calculatorForm: FormGroup;
	public exceedsMaxPAW: boolean;
	public totalReferenceET: number;
	public originalRecommendation: number;
	public recommendation: number;
	private _cropTypeViewModel: ICropTypeViewModel;

	public irrigationMethodOptions: {
		Text: string,
		Id: number,
		Rate: number,
		DU: number
	}[];

	public isEventDateFuture: boolean;
	private DEFAULT_COORDINATE = '37.020714, -119.445638';

	public icons: {
		selected: IMapIcon,
		unselected: IMapIcon
	}

	public wetDateRange: {
		minDate: Date,
		maxDate: Date
	}

	public harvestDateRange: {
		minDate: Date,
		maxDate: Date
	}

	public eventDateRange: {
		minDate: Date,
		maxDate: Date
	}

	public previousDateRange: {
		minDate: Date,
		maxDate: Date
	}

	public form: FormGroup;

	public get f(): { [key in ETCalculatorFields]: AbstractControl } {
		return this.form.controls as { [key in ETCalculatorFields]: AbstractControl };
	}

	private isTree: boolean;
	private MAX_DATE_RANGE = 365;
	private _subscriptions$: Subject<boolean>;

	constructor(
		private _cdr: ChangeDetectorRef,
		private _commodityService: CommodityService,
		private _cropTypeService: CropTypeService,
		private _dialog: MatDialog,
		private _fb: FormBuilder,
		private _irrigationService: IrrigationService,
		private _notificationService: NotificationService,
		private _persistentDatabase: PersistentDatabase,
		private _translateService: TranslateService,
		private _updateService: UpdateService,
		private _weatherStationsService: WeatherStationsService,
	) {
		let iconDimensions: IMapIconSize;

		iconDimensions = {
			height: 50,
			width: 80
		};

		this.wetDateRange = {
			minDate: null,
			maxDate: null, // DateUtility.subtractDays(new Date(), 1)
		};

		this.harvestDateRange = {
			minDate: null,
			maxDate: null
		};

		this.eventDateRange = {
			minDate: null,
			maxDate: null
		};

		this.previousDateRange = {
			minDate: null,
			maxDate: null
		};

		this.icons = {
			selected: {
				url: '../assets/images/icons/map_marker-weather_station-selected.png',
				scaledSize: iconDimensions
			},
			unselected: {
				url: '../assets/images/icons/map_marker-weather_station.png',
				scaledSize: iconDimensions
			}
		}

		this.weatherStations = [];
		this.isTree = false;
		this.isEventDateFuture = null;

		this.irrigationMethodOptions = [
			{ Text: 'Drip', Id: eIrrigationMethods.DRIP, Rate: 0.15, DU: 90 },
			{ Text: 'Micro Sprinkler', Id: eIrrigationMethods.MICRO_SPRINKLER, Rate: 0.3, DU: 75 },
			{ Text: 'Sprinkler', Id: eIrrigationMethods.SPRINKLER, Rate: 0.3, DU: 75 }
		];

		this.calculatorSettings = PlantingAdvancedEdit.CalculatorSettingsDefault;
		this.exceedsMaxPAW = false;
	}

	ngOnInit(): void {
		this._subscriptions$ = new Subject();
		this._initializeForm();

		this._updateService.setCurrentPage(navigationViews.IR_CALCULATOR);

		let calculatorSettingsFormGenerator = new CalculatorSettingsForm(this._fb, this._subscriptions$);
		this.calculatorForm = calculatorSettingsFormGenerator.initialize(this.calculatorSettings);

		this._getWeatherstations().then(() => {
			this._getUserLocation().then(() => {
				this._loadPersistentData();
			});
		});

		this._commodityService.list().then((response) => {
			// don't offer alfalfa for now, which requires cut events
			this.commodityTypes = response.filter(x => x.CommodityTypeCalculator !== eCommodityTypeCalculators.alfalfa);
		});
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	public calculate() {
		let data: SingleCallIrrigationRecommendationModel;

		if (!this.form.valid) {
			this._notificationService.generateNotifcation({
				type: eNotificationTypes.ERROR,
				message: 'Please fill in all required fields before calculating'
			});
			return;
		}

		if (this.f.Company.value) {
			return;
		}

		this.isSaving = true;
		this.singleCallEvapRecords = [];

		data = this.form.value as SingleCallIrrigationRecommendationModel;

		data.SoilProperties = {
			Sand: 0,
			Silt: 0
		};

		data.IrrigationEvents = [];

		data.IrrigationEvents.push({
			EventDate: this.form.get('PreviousDate').value,
			MethodId: 1,
			Applied: 0
		});

		this._irrigationService.singleCallAPI(data).then((response) => {
			this.singleCallResult = response;
			this.originalRecommendation = this.singleCallResult.RecommendedIrrigationAmount;

			if (this.originalRecommendation > this.singleCallResult.MaxPAW) {
				this.exceedsMaxPAW = true;
				this.recommendation = this.singleCallResult.MaxPAW;
				this._convertToHours(this.singleCallResult.MaxPAW);
			} else {
				this.exceedsMaxPAW = false;
				this.recommendation = this.originalRecommendation;
				this._convertToHours(this.originalRecommendation);
			}

			return this._irrigationService.getIrrigationChart(data).catch((): EvaporationRecordSimple[] => []);
	}).then((dailyRows) => {
		this.singleCallEvapRecords = dailyRows && dailyRows.length > 0 ? dailyRows : [];
		this.totalReferenceET = this.singleCallEvapRecords.reduce((sum, r) => sum + (r.ETo || 0), 0);
		this._cdr.detectChanges();
		this._drawSingleCallEvaporationChart(this.singleCallEvapRecords);
	}).catch(() => {
			// Errors are typically surfaced by the HTTP layer; ensure loading state clears.
		}).then(() => {
			this.isSaving = false;
		});
	}

	private _drawSingleCallEvaporationChart(records: EvaporationRecordSimple[]): void {
		let series;

		if (!records || records.length === 0 || !this._singleCallEvapChart) {
			return;
		}

		series = this._evaporationRecordsToChartSeries(records);
		// Compact height so the chart stays visually comparable to the summary table beside it.
		HighChartsService.drawGraph(this._singleCallEvapChart, series, undefined, 260);
	}

	/** Daily rain & reference ET plus optional cumulative series (off by default; toggle in legend). */
	private _evaporationRecordsToChartSeries(records: EvaporationRecordSimple[]): Highcharts.IndividualSeriesOptions[] {
		let rain: IHighChartCustomSeries;
		let referenceEt: IHighChartCustomSeries;
		let cumulativeReferenceEt: IHighChartCustomSeries;
		let cumulativeRain: IHighChartCustomSeries;
		let runningETo: number;
		let runningRain: number;

		rain = {
			name: 'Rainfall',
			type: 'line',
			visible: false,
			data: [],
			tooltip: {
				valueDecimals: 2
			}
		};
		referenceEt = {
			name: 'Reference ET (ETo)',
			type: 'line',
			visible: true,
			data: [],
			tooltip: {
				valueDecimals: 2
			}
		};
		cumulativeReferenceEt = {
			name: 'Cumulative reference ET (ETo)',
			type: 'line',
			visible: false,
			data: [],
			tooltip: {
				valueDecimals: 2
			}
		};
		cumulativeRain = {
			name: 'Cumulative rain',
			type: 'line',
			visible: false,
			data: [],
			tooltip: {
				valueDecimals: 2
			}
		};

		if (!records || records.length === 0) {
			return [];
		}

		runningETo = 0;
		runningRain = 0;

		for (const record of records) {
			runningETo += record.ETo;
			runningRain += record.Rain != null ? record.Rain : 0;

			rain.data.push([DateUtility.DateToUTC(record.Date), record.Rain != null ? record.Rain : 0]);
			referenceEt.data.push([DateUtility.DateToUTC(record.Date), record.ETo]);
			cumulativeReferenceEt.data.push([DateUtility.DateToUTC(record.Date), runningETo]);
			cumulativeRain.data.push([DateUtility.DateToUTC(record.Date), runningRain]);
		}

		return [rain, referenceEt, cumulativeReferenceEt, cumulativeRain];
	}

	public mapReady() {
		this._centerMap(this.coordinatesUser);
	}

	public clickMap(event: IAgmMapClick) {
		if (!this.weatherStations || this.weatherStations.length === 0) {
			this._notificationService.generateNotifcation({
				type: eNotificationTypes.ERROR,
				message: `Please wait until weather stations data have loaded.`
			});
		}

		this.f.Latitude.setValue(event.coords.lat);
		this.f.Longitude.setValue(event.coords.lng);

		this._sortStationsByDistance();
		this.f.WeatherStationId.setValue(this.weatherStations[0].Id);
	}

	public getCropTypeName(): string {
		if (!this.cropTypes || this.cropTypes.length === 0 || !this.f.CropTypeId.value) {
			return '';
		}

		return this.cropTypes.find(x => x.Id === this.f.CropTypeId.value).Name;
	}

	public openApplicationCalculator(irrigationMethodId: number): void {
		const isTree = CropType.isTree(this.f.CommodityTypeId.value);

		let dialogConfig: MatDialogConfig = {
			data: {
				form: null,
				control: this.f.ApplicationRate,
			},
			width: '600px',
			disableClose: true
		};

		switch (irrigationMethodId) {
			case eIrrigationMethods.DRIP:

				if (isTree) {
					dialogConfig.data.form = this.calculatorForm.get('DripApplicationRate');
					this._dialog.open(DripApplicationRateTreeComponent, dialogConfig);
				} else {
					dialogConfig.data.form = this.calculatorForm.get('DripApplicationRate');
					this._dialog.open(DripApplicationRateComponent, dialogConfig);
				}
				break;
			case eIrrigationMethods.SPRINKLER:
				dialogConfig.data.form = this.calculatorForm.get('SprinklerApplicationRate');
				this._dialog.open(SprinklerApplicationRateComponent, dialogConfig);
				break;
			case eIrrigationMethods.MICRO_SPRINKLER:
				dialogConfig.data.form = this.calculatorForm.get('MicroSprinklerSetting');
				this._dialog.open(MicroSprinklerComponent, dialogConfig);
				break;
		}
	}

	private _loadPersistentData() {
		let params: ETCalculatorResultsInputParams;

		if (!this._persistentDatabase.etCalculatorParams) {
			return;
		}

		params = this._persistentDatabase.etCalculatorParams;

		this.f.AgeOfCrop.setValue(params.data.AgeOfCrop);
		this.f.CommodityTypeId.setValue(params.CommodityTypeId);
		this.f.CropTypeId.setValue(params.data.CropTypeId);
		this.f.HarvestDate.setValue(params.data.HarvestDate);
		this.f.WetDate.setValue(params.data.WetDate);
		this.f.WeatherStationId.setValue(params.data.WeatherStationId);
		this.f.Latitude.setValue(params.data.Latitude);
		this.f.Longitude.setValue(params.data.Longitude);

		this._sortStationsByDistance();
		this.f.WeatherStationId.setValue(this.weatherStations[0].Id);

		let coords: Coordinates;

		coords = new Coordinates(this.f.Latitude.value + ',' + this.f.Longitude.value);
		this._centerMap(coords);
	}

	private _sortStationsByDistance() {
		this.weatherStations.forEach(e => {
			e.Distance = this._calculateDistance(e.Lat, e.Lng, this.f.Latitude.value, this.f.Longitude.value);
		});

		this.weatherStations.sort(function (a, b) {
			return a.Distance - b.Distance;
		});
	}

	private _getRad(x: number): number {
		return x * Math.PI / 180;
	}

	private _calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number) {
		let result: number;

		const EARTH_RADIUS = 6371; // km
		const dLat = this._getRad(lat2 - lat1);
		const dLon = this._getRad(lon2 - lon1);
		const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
			+ Math.cos(this._getRad(lat1)) * Math.cos(this._getRad(lat2))
			* Math.sin(dLon / 2) * Math.sin(dLon / 2);

		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		result = EARTH_RADIUS * c;

		return result;
	}

	private _getWeatherstations(): Promise<void> {
		return this._weatherStationsService.listActivePublic().then((stations) => {
			this.weatherStations = stations;
		});
	}

	private _getUserLocation(): Promise<void> {
		return GeoLocation.getCoordinates().then(position => {
			if (!position.coords.latitude || !position.coords.longitude) {
				return;
			}

			this._updateService.setCurrentLocation(Number(position.coords.latitude),
				Number(position.coords.longitude));

			this.coordinatesUser = new Coordinates(this._updateService.currentLocation.lat
				+ ',' + this._updateService.currentLocation.lng);

			this.mapZoom = 10;
			this._centerMap(this.coordinatesUser);
		}).catch((reason: any) => {
			this.coordinatesUser = new Coordinates(this.DEFAULT_COORDINATE);
			this.mapZoom = 10;
			this._centerMap(this.coordinatesUser);
		});
	}

	private _initializeForm() {
		this.form = this._fb.group({
			AgeOfCrop: [ null, [Validators.required]],
			CommodityTypeId: [null, [Validators.required, FormValidation.greaterThanValidator(0)]],
			CropTypeId: [null, [Validators.required, FormValidation.isDate, FormValidation.greaterThanValidator(0)]],
			WetDate: [null, [Validators.required]],
			HarvestDate: [null],
			EventDate: [null, [Validators.required]],
			PreviousDate: [null],
			Latitude: [null, [Validators.required]],
			Longitude: [null, [Validators.required]],
			WeatherStationId: [null, [Validators.required]],
			DistributionUniformity: [90, [Validators.required, Validators.min(0), Validators.max(100)]],
			IrrigationMethodId: [null, [Validators.required]],
			ApplicationRate: [null, [Validators.required]],
			Company: [''] // honeypot
		});

		this.f.HarvestDate.setValidators([Validators.required,
			FormValidation.isDate,
			FormValidation.mustBeAfterDate(this.f.WetDate),
			FormValidation.mustBeWithinDateRange(this.f.WetDate, this.MAX_DATE_RANGE)]);

		this.f.CommodityTypeId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			let item: ICommodityType;

			item = this.commodityTypes.find(x => x.Id === this.f.CommodityTypeId.value);
			this.isTree = CropType.isTreeByInterface(item.CommodityTypeCalculator);

			if (this.isTree) {
				this.f.AgeOfCrop.enable();
			} else {
				this.f.AgeOfCrop.disable();
			}

			this._cropTypeService.getCropTypesAdmin(false, this.f.CommodityTypeId.value).then((response) => {
				this.cropTypes = response;
			});
		});

		this.f.CropTypeId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			if (!this.cropTypes) {
				return;
			}

			this._cropTypeService.getCropModel(this.f.CropTypeId.value).then((res) => {
				this._cropTypeViewModel = res;

				if (this.isTree) {
					let item: ICropTypeListItem;

					item = this.cropTypes.find(x => x.Id === this.f.CropTypeId.value);

					this.ageOfCropOptions = PlantingAdvancedEdit.generateAgeOfCropDropdown(item.MaxAge,
						this._translateService);
				} else {
					this.ageOfCropOptions = [];
				}
			});
		});

		this.f.WetDate.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.harvestDateRange.minDate = DateUtility.addDays(this.f.WetDate.value, 1); // end date should not cross over start date
			this.harvestDateRange.maxDate = this._getHarvestDateMax(this.f.WetDate.value); // end date should not be out of range
			this.previousDateRange.minDate = this.f.WetDate.value;

			if (!this.f.HarvestDate.value) {
				this.f.HarvestDate.setValue(Planting.setHarvestDateFromDaysToEndDate(
					this._cropTypeViewModel.DaysToEndDate,
					this.f.WetDate.value, this.isTree), {emitEvent: true});
			}
		});

		this.f.HarvestDate.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.wetDateRange.minDate = DateUtility.addDays(this.f.HarvestDate.value, -this.MAX_DATE_RANGE);
			this.wetDateRange.maxDate = DateUtility.addDays(this.f.HarvestDate.value, -1);
			this.previousDateRange.maxDate = this.f.HarvestDate.value;
			this._setMaxEventDate();
		});

		this.f.EventDate.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.isEventDateFuture = DateUtility.isFuture(this.f.EventDate.value);
			this.previousDateRange.maxDate = DateUtility.addDays(this.f.EventDate.value, -1);
		});

		this.f.PreviousDate.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.eventDateRange.minDate = DateUtility.addDays(this.f.PreviousDate.value, 1);
			this._setMaxEventDate();
		});

		this.f.IrrigationMethodId.valueChanges.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			let option = this.irrigationMethodOptions.find(x => x.Id === this.f.IrrigationMethodId.value);

			this.f.ApplicationRate.setValue(option.Rate);
			this.f.DistributionUniformity.setValue(option.DU);
		});
	}

	private _setMaxEventDate(): void {
		let previousDate: Date;
		let harvestDate: Date;

		previousDate = this.f.PreviousDate.value as Date;
		harvestDate = this.f.HarvestDate.value;

		let maxDateFromPrevious: Date = null;

		if (previousDate) {
			maxDateFromPrevious = DateUtility.addDays(previousDate, 30);
		}

		if (maxDateFromPrevious && harvestDate) {
			this.eventDateRange.maxDate = maxDateFromPrevious < harvestDate ? maxDateFromPrevious : harvestDate;
		} else if (maxDateFromPrevious) {
			this.eventDateRange.maxDate = maxDateFromPrevious;
		} else if (harvestDate) {
			this.eventDateRange.maxDate = harvestDate;
		} else {
			this.eventDateRange.maxDate = null;
		}
	}

	private _convertToHours(inches: number) {
		let hoursFloat: number;

		hoursFloat = inches / this.f.ApplicationRate.value;
		this.hours = Math.floor(hoursFloat);
		this.minutes = Math.round((hoursFloat - this.hours) * 60);
	}

	private _getHarvestDateMax(wetDate: Date): Date {
		let maxDateInRange: Date;

		maxDateInRange = DateUtility.addDays(this.f.WetDate.value, this.MAX_DATE_RANGE);

		return maxDateInRange;
	}

	private _centerMap(coordinates: Coordinates): void {
		const ZOOM_LEVEL = 15;

		if (!this.mapControl || !this.map || !coordinates) {
			return;
		}

		this.map.triggerResize().then(() => {
			this.mapControl.centerMap(coordinates.lat, coordinates.lng);
			this.mapControl.setZoom(ZOOM_LEVEL);
		});
	}
}
